import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { resetDefaultProfile } from '../api/user-logout';
import { clearUsersData } from '../utils';
import { authConfig } from '../utils/auth-configs';

export type AuthContextValue = {
  isAuthenticated: boolean;
  isTokenInitialized: boolean;
  logout: () => void;
  login: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
  mockProvider?: boolean;
};

function InnerAuthProvider({ children, mockProvider }: AuthProviderProps) {
  const { keycloak } = mockProvider
    ? {
        keycloak: {
          authenticated: true,
          didInitialize: true,
          login: () => {},
          logout: () => {},
        },
      }
    : useKeycloak();

  const logout = useCallback(async () => {
    try {
      await resetDefaultProfile();
    } finally {
      clearUsersData();
      await keycloak.logout({
        redirectUri: window.location.origin,
      });
    }
  }, [keycloak]);

  const login = useCallback(() => {
    keycloak.login();
  }, [keycloak]);

  const value = useMemo(
    () => ({
      isAuthenticated: !!keycloak.authenticated,
      isTokenInitialized: keycloak.didInitialize,
      login,
      logout,
    }),
    [keycloak.authenticated, keycloak.didInitialize, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthProvider({ children, mockProvider }: AuthProviderProps) {
  if (mockProvider) {
    return <InnerAuthProvider mockProvider>{children}</InnerAuthProvider>;
  }

  return (
    <ReactKeycloakProvider
      initOptions={{
        onLoad: 'login-required',
      }}
      authClient={authConfig}
    >
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </ReactKeycloakProvider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthContext');
  }
  return context;
}

export { AuthContext, AuthProvider, useAuth };
